import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { uploadOptimizedImage } from "@/lib/image-upload";
import {
  getGroupsEventsPageData,
  upsertGroupsEventsContent,
  upsertGroupsEventsCard,
  deleteGroupsEventsCard,
  reorderGroupsEventsCards,
  upsertGroupsEventsFaq,
  deleteGroupsEventsFaq,
  reorderGroupsEventsFaqs,
  upsertGroupsEventsGallery,
  deleteGroupsEventsGalleryImage,
  reorderGroupsEventsGallery,
  type GroupsEventsContent,
  type GroupsEventsCard,
  type GroupsEventsFaq,
  type GroupsEventsGalleryImage,
} from "@/lib/groups-events.functions";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Loader2,
  Plus,
  Pencil,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/groups-events")({
  head: () => ({
    meta: [
      { title: "Groups & Events CMS" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminGroupsEventsPage,
});

async function uploadTo(folder: string, file: File): Promise<string> {
  const { url } = await uploadOptimizedImage({
    file,
    bucket: "slide-media",
    folder: `groups-events/${folder}`,
  });
  return url;
}

function AdminGroupsEventsPage() {
  const fetch = useServerFn(getGroupsEventsPageData);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "groups-events"],
    queryFn: () => fetch(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "groups-events"] });
    qc.invalidateQueries({ queryKey: ["groups-events", "page"] });
  };

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-background p-10 text-foreground/60">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        <div>
          <Link
            to="/admin"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <h1 className="text-2xl font-serif">Groups & Events CMS</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit the Groups & Events hero, event type cards, FAQ, and gallery.
          </p>
        </div>

        <HeroPanel content={data.content} onSaved={invalidate} />
        <CardsPanel cards={data.cards} onChanged={invalidate} />
        <FaqsPanel faqs={data.faqs} onChanged={invalidate} />
        <GalleryPanel gallery={data.gallery} onChanged={invalidate} />
      </div>
    </div>
  );
}

/* ---------- Hero ---------- */

function HeroPanel({
  content,
  onSaved,
}: {
  content: GroupsEventsContent | null;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertGroupsEventsContent);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [heroUrl, setHeroUrl] = useState(content?.hero_image_url ?? "");
  const [kicker, setKicker] = useState(content?.hero_kicker ?? "");
  const [title, setTitle] = useState(content?.hero_title ?? "");
  const [subtitle, setSubtitle] = useState(content?.hero_subtitle ?? "");
  const [ctaLabel, setCtaLabel] = useState(content?.hero_cta_label ?? "");

  const buildPayload = (urlOverride?: string) => ({
    id: content?.id,
    hero_image_url: (urlOverride ?? heroUrl) || null,
    hero_kicker: kicker || null,
    hero_title: title || null,
    hero_subtitle: subtitle || null,
    hero_cta_label: ctaLabel || null,
    intro_body: content?.intro_body ?? null,
  });

  const save = useMutation({
    mutationFn: () => upsert({ data: buildPayload() }),
    onSuccess: () => {
      toast.success("Saved");
      onSaved();
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const url = await uploadTo("hero", f);
      setHeroUrl(url);
      await upsert({ data: buildPayload(url) });
      toast.success("Hero image saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-5">
      <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        Hero
      </h2>

      <div>
        <Label className="mb-2 block">Hero image</Label>
        <div className="flex items-start gap-3">
          {heroUrl ? (
            <img
              src={heroUrl}
              alt=""
              className="w-32 h-32 object-cover rounded"
            />
          ) : (
            <div className="w-32 h-32 rounded bg-muted" />
          )}
          <div className="flex-1 space-y-2">
            <Input
              value={heroUrl}
              onChange={(e) => setHeroUrl(e.target.value)}
              placeholder="https://… or upload"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Upload
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Kicker</Label>
        <Input
          value={kicker}
          onChange={(e) => setKicker(e.target.value)}
          placeholder="Private Groups & Events"
        />
      </div>
      <div>
        <Label className="mb-2 block">Title</Label>
        <Textarea
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          rows={2}
          placeholder={"Private Groups & Events at Backland\n(press Enter to break line)"}
        />
      </div>
      <div>
        <Label className="mb-2 block">Subtitle</Label>
        <Textarea
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label className="mb-2 block">CTA button label</Label>
        <Input
          value={ctaLabel}
          onChange={(e) => setCtaLabel(e.target.value)}
          placeholder="Inquire About an Event"
        />
      </div>

      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save
      </Button>
    </div>
  );
}

/* ---------- Event cards ---------- */

function CardsPanel({
  cards,
  onChanged,
}: {
  cards: GroupsEventsCard[];
  onChanged: () => void;
}) {
  const reorder = useServerFn(reorderGroupsEventsCards);
  const del = useServerFn(deleteGroupsEventsCard);
  const [editing, setEditing] = useState<GroupsEventsCard | null>(null);
  const [creating, setCreating] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...cards];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((c) => c.id) } });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Event type cards
        </h2>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add card
        </Button>
      </div>

      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No cards yet.</p>
      ) : (
        <div className="space-y-2">
          {cards.map((c, i) => (
            <div
              key={c.id}
              className="flex items-center gap-3 rounded-md border border-border bg-background p-2"
            >
              {c.image_url ? (
                <img
                  src={c.image_url}
                  alt=""
                  className="w-16 h-16 object-cover rounded"
                />
              ) : (
                <div className="w-16 h-16 rounded bg-muted" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-serif truncate">{c.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {c.slug}
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                disabled={i === cards.length - 1}
                onClick={() => move(i, 1)}
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setEditing(c)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={async () => {
                  if (!confirm(`Delete "${c.title}"?`)) return;
                  await del({ data: { id: c.id } });
                  onChanged();
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {(editing || creating) && (
        <CardEditor
          card={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function CardEditor({
  card,
  onClose,
  onSaved,
}: {
  card: GroupsEventsCard | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertGroupsEventsCard);
  const [slug, setSlug] = useState(card?.slug ?? "");
  const [title, setTitle] = useState(card?.title ?? "");
  const [description, setDescription] = useState(card?.description ?? "");
  const [bestFor, setBestFor] = useState(card?.best_for ?? "");
  const [cta, setCta] = useState(card?.cta_label ?? "");
  const [image, setImage] = useState(card?.image_url ?? "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const autoSlug = (val: string) =>
    val
      .toLowerCase()
      .replace(/['']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const url = await uploadTo(`card/${slug || autoSlug(title) || "new"}`, f);
      setImage(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    const finalSlug = slug.trim() || autoSlug(title);
    if (!/^[a-z0-9-]+$/.test(finalSlug)) {
      toast.error("Slug must be lowercase letters, numbers, and dashes");
      return;
    }
    setSaving(true);
    try {
      await upsert({
        data: {
          id: card?.id,
          slug: finalSlug,
          title: title.trim(),
          description: description || null,
          best_for: bestFor || null,
          cta_label: cta || null,
          image_url: image || null,
        },
      });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{card ? "Edit card" : "Add card"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Image</Label>
            <div className="flex items-start gap-3">
              {image ? (
                <img
                  src={image}
                  alt=""
                  className="w-24 h-24 object-cover rounded"
                />
              ) : (
                <div className="w-24 h-24 rounded bg-muted" />
              )}
              <div className="flex-1 space-y-2">
                <Input
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                  placeholder="https://…"
                />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Slug</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={autoSlug(title) || "weddings"}
            />
          </div>
          <div>
            <Label className="mb-2 block">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div>
            <Label className="mb-2 block">Best for</Label>
            <Textarea
              value={bestFor}
              onChange={(e) => setBestFor(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label className="mb-2 block">CTA button label</Label>
            <Input
              value={cta}
              onChange={(e) => setCta(e.target.value)}
              placeholder="View Sample"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- FAQs ---------- */

function FaqsPanel({
  faqs,
  onChanged,
}: {
  faqs: GroupsEventsFaq[];
  onChanged: () => void;
}) {
  const reorder = useServerFn(reorderGroupsEventsFaqs);
  const del = useServerFn(deleteGroupsEventsFaq);
  const [editing, setEditing] = useState<GroupsEventsFaq | null>(null);
  const [creating, setCreating] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...faqs];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((f) => f.id) } });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          FAQ
        </h2>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add FAQ
        </Button>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        First FAQ shows above the "See more FAQ" toggle on the live page. The
        three structured sections (fit / activities / what Backland provides) are
        not managed here.
      </p>

      {faqs.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No FAQs yet.</p>
      ) : (
        <div className="space-y-2">
          {faqs.map((f, i) => (
            <div
              key={f.id}
              className="flex items-start gap-3 rounded-md border border-border bg-background p-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.question}</div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                  {f.answer}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={i === faqs.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setEditing(f)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={async () => {
                    if (!confirm("Delete this FAQ?")) return;
                    await del({ data: { id: f.id } });
                    onChanged();
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || creating) && (
        <FaqEditor
          faq={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function FaqEditor({
  faq,
  onClose,
  onSaved,
}: {
  faq: GroupsEventsFaq | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertGroupsEventsFaq);
  const [question, setQuestion] = useState(faq?.question ?? "");
  const [answer, setAnswer] = useState(faq?.answer ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Question and answer are required");
      return;
    }
    setSaving(true);
    try {
      await upsert({
        data: {
          id: faq?.id,
          question: question.trim(),
          answer: answer.trim(),
        },
      });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{faq ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Question</Label>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-2 block">Answer</Label>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={6}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Gallery ---------- */

function GalleryPanel({
  gallery,
  onChanged,
}: {
  gallery: GroupsEventsGalleryImage[];
  onChanged: () => void;
}) {
  const reorder = useServerFn(reorderGroupsEventsGallery);
  const del = useServerFn(deleteGroupsEventsGalleryImage);
  const upsert = useServerFn(upsertGroupsEventsGallery);
  const [editing, setEditing] = useState<GroupsEventsGalleryImage | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...gallery];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((g) => g.id) } });
    onChanged();
  };

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const url = await uploadTo("gallery", f);
        await upsert({ data: { image_url: url, alt: null } });
      }
      toast.success("Uploaded");
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Gallery
        </h2>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) handleUpload(e.target.files);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-1" />
            )}
            Upload
          </Button>
        </div>
      </div>

      {gallery.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No images yet. Upload some to populate the bottom gallery.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {gallery.map((g, i) => (
            <div
              key={g.id}
              className="relative rounded-md border border-border overflow-hidden bg-background"
            >
              <img
                src={g.image_url}
                alt={g.alt ?? ""}
                className="w-full aspect-square object-cover"
              />
              <div className="flex items-center justify-between p-1.5 bg-background/95">
                <div className="flex">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled={i === gallery.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => setEditing(g)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={async () => {
                      if (!confirm("Delete this image?")) return;
                      await del({ data: { id: g.id } });
                      onChanged();
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <GalleryEditor
          image={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function GalleryEditor({
  image,
  onClose,
  onSaved,
}: {
  image: GroupsEventsGalleryImage;
  onClose: () => void;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertGroupsEventsGallery);
  const [url, setUrl] = useState(image.image_url);
  const [alt, setAlt] = useState(image.alt ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await upsert({
        data: { id: image.id, image_url: url, alt: alt || null },
      });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit image</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <img
            src={url}
            alt={alt}
            className="w-full aspect-video object-cover rounded"
          />
          <div>
            <Label className="mb-2 block">Image URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Alt text</Label>
            <Input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Describe the image for accessibility"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
